import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Modal, StatusBadge } from "@fawkes/ui";
import {
  ChevronDown, Home, Mail, MapPin, MessageCircleQuestion, PawPrint, Phone, UserRound, Users,
  type LucideProps,
} from "lucide-react";

import { formatDate } from "../i18n/format";
import { KennismakingExtraVragen } from "./KennismakingExtraVragen";

/** Een reactie in de lijst — spiegelt api {@code ReactieRijResponse}. */
interface ReactieRij {
  id: string;
  status: string;
  contactNaam: string | null;
  contactEmail: string | null;
  contactTelefoon: string | null;
  contactPlaats: string | null;
  ingediendOp: string | null;
  gelezenOp: string | null;
  interneNotitie: string | null;
}

interface Antwoord {
  sectie: string | null;
  vraag: string | null;
  antwoord: string | null;
}

interface ReactieDetail {
  reactie: ReactieRij;
  antwoorden: Antwoord[];
}

/**
 * De reacties bij één advertentie: wie wil dit dier leren kennen, en wat vulde hij in.
 *
 * <p>Doorklik vanuit het advertentieoverzicht (Maarten, 2026-10-28). Bewust geen eigen pagina: je
 * komt hier om één dier te bekijken en gaat daarna terug naar de lijst, en een scherm dat je
 * wegklikt houdt die lijst met zijn filters in stand.
 *
 * <p><b>Er wordt geen relatie aangemaakt.</b> Iemand die een formulier invult is nog geen klant van
 * de opvang, en wie belangstelling toont hoort niet ongevraagd in het klantenbestand te belanden.
 * Daarom staan de contactgegevens hier, bij de reactie, en nergens anders.
 */
export function KennismakingReacties({
  advertentieId,
  dierNaam,
  onClose,
  onGelezen,
}: {
  advertentieId: string;
  dierNaam: string | null;
  onClose: () => void;
  /** Geroepen nadat een reactie op gelezen is gezet, zodat het overzicht zijn teller bijwerkt. */
  onGelezen?: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [open, setOpen] = useState<string | null>(null);
  const [notitie, setNotitie] = useState("");
  const [notitieVan, setNotitieVan] = useState<string | null>(null);

  const { data, isLoading, refetch } = useCustom<ReactieRij[]>({
    url: `herplaatsing/${advertentieId}/reacties`,
    method: "get",
  });
  const rijen = data?.data ?? [];

  const { data: detailData, isLoading: detailBezig } = useCustom<ReactieDetail>({
    url: `herplaatsing/reacties/${open ?? ""}`,
    method: "get",
    queryOptions: { enabled: open != null },
  });
  const detail = open == null ? undefined : detailData?.data;

  const { mutate: muteer, isLoading: bezig } = useCustomMutation();

  // Openen is lezen. Dat gebeurt hier en niet bij het opslaan van een notitie: de vraag die het
  // overzicht beantwoordt is "heeft iemand hier al naar gekeken", en dat is waar bij openen.
  useEffect(() => {
    if (open == null) return;
    const rij = rijen.find((r) => r.id === open);
    if (rij == null || rij.gelezenOp != null) return;
    muteer(
      {
        url: `herplaatsing/reacties/${open}/gelezen`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          void refetch();
          onGelezen?.();
        },
      },
    );
    // rijen hoort hier niet bij: de verversing die hierop volgt zou deze haak anders opnieuw starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // De notitie uit de geopende reactie in het tekstvak, maar alleen bij wisselen van reactie —
  // anders overschrijft een verversing wat iemand net aan het typen is.
  useEffect(() => {
    if (detail?.reactie == null) return;
    if (notitieVan === detail.reactie.id) return;
    setNotitie(detail.reactie.interneNotitie ?? "");
    setNotitieVan(detail.reactie.id);
  }, [detail, notitieVan]);

  const bewaarNotitie = () => {
    if (open == null) return;
    muteer(
      {
        url: `herplaatsing/reacties/${open}/notitie`,
        method: "put",
        values: { tekst: notitie },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => void refetch() },
    );
  };

  // De antwoorden per sectie, in de volgorde waarin ze binnenkwamen: dat is de volgorde waarin de
  // belangstellende ze zag, en die leest het prettigst terug.
  const secties = useMemo(() => {
    const uit: { sectie: string; antwoorden: Antwoord[] }[] = [];
    for (const a of detail?.antwoorden ?? []) {
      const naam = a.sectie ?? "";
      const laatste = uit[uit.length - 1];
      if (laatste != null && laatste.sectie === naam) laatste.antwoorden.push(a);
      else uit.push({ sectie: naam, antwoorden: [a] });
    }
    return uit;
  }, [detail]);

  const nieuw = rijen.filter((r) => r.gelezenOp == null).length;

  return (
    <Modal
      open
      size="wide"
      title={t("kennismaking.titel").replace("%naam%", dierNaam ?? t("kennismaking.ditDier"))}
      subtitle={
        isLoading
          ? undefined
          : t(rijen.length === 1 ? "kennismaking.aantal.een" : "kennismaking.aantal")
              .replace("%n%", String(rijen.length))
              .replace("%nieuw%", String(nieuw))
      }
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("kennismaking.sluiten")}
        </Button>
      }
    >
      {/* De extra vragen staan boven de reacties en niet eronder: ze bepalen wat er binnenkomt, en
          ze horen er ook te staan als er nog niets binnen is. Dat lege geval is juist het moment
          waarop je ze wilt zetten (Maarten, 2026-10-29). */}
      <KennismakingExtraVragen advertentieId={advertentieId} />

      {isLoading ? (
        <p className="fawkes-kennis__leeg">{t("common.loading")}</p>
      ) : rijen.length === 0 ? (
        <p className="fawkes-kennis__leeg">{t("kennismaking.geen")}</p>
      ) : (
        <div className="fawkes-kennis">
          <ul className="fawkes-kennis__lijst">
            {rijen.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={
                    r.id === open ? "fawkes-kennis__rij is-open" : "fawkes-kennis__rij"
                  }
                  onClick={() => setOpen(r.id === open ? null : r.id)}
                >
                  {/* Een gezicht bij de naam: dit zijn mensen, en een lijst van kale tekstregels
                      leest als administratie. */}
                  <span className="fawkes-kennis__avatar" aria-hidden="true">
                    <UserRound size={20} />
                  </span>
                  <span className="fawkes-kennis__rijtekst">
                    <span className="fawkes-kennis__naam">
                      {r.contactNaam ?? t("kennismaking.zonderNaam")}
                      {r.gelezenOp == null && (
                        <StatusBadge tone="warning">{t("kennismaking.nieuw")}</StatusBadge>
                      )}
                    </span>
                    <span className="fawkes-kennis__meta">
                      {[r.contactPlaats, r.ingediendOp == null ? null : formatDate(r.ingediendOp, locale)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  {r.interneNotitie != null && r.interneNotitie.trim() !== "" && (
                    <span className="fawkes-kennis__notitieteken">
                      {t("kennismaking.heeftNotitie")}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="fawkes-kennis__detail">
            {open == null ? (
              <p className="fawkes-kennis__leeg">{t("kennismaking.kiesEen")}</p>
            ) : detailBezig || detail == null ? (
              <p className="fawkes-kennis__leeg">{t("common.loading")}</p>
            ) : (
              <>
                {/* Bellen of mailen is wat er met een reactie gebeurt, dus dat staat bovenaan en
                    aanklikbaar — niet als tekst die je zelf moet overtypen. */}
                <div className="fawkes-kennis__contact">
                  <span className="fawkes-kennis__avatar fawkes-kennis__avatar--groot" aria-hidden="true">
                    <UserRound size={30} />
                  </span>
                  <div className="fawkes-kennis__contactbody">
                    <div className="fawkes-kennis__contactkop">
                      <strong>{detail.reactie.contactNaam ?? t("kennismaking.zonderNaam")}</strong>
                      {detail.reactie.contactPlaats && (
                        <span className="fawkes-kennis__plaats">
                          <MapPin size={13} aria-hidden="true" />
                          {detail.reactie.contactPlaats}
                        </span>
                      )}
                    </div>
                    <div className="fawkes-kennis__bereik">
                      {detail.reactie.contactTelefoon && (
                        <a href={`tel:${detail.reactie.contactTelefoon.replace(/\s/g, "")}`}>
                          <Phone size={15} aria-hidden="true" />
                          {detail.reactie.contactTelefoon}
                        </a>
                      )}
                      {detail.reactie.contactTelefoon && detail.reactie.contactEmail && (
                        <span className="fawkes-kennis__scheider" aria-hidden="true">|</span>
                      )}
                      {detail.reactie.contactEmail && (
                        <a href={`mailto:${detail.reactie.contactEmail}`}>
                          <Mail size={15} aria-hidden="true" />
                          {detail.reactie.contactEmail}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Per sectie een kaart, standaard open en in te klappen: wie terugbelt leest van
                    boven naar beneden, wie iets opzoekt wil de rest weg kunnen doen. */}
                {secties.map((sec) => {
                  const SectieIcoon = sectieIcoon(sec.sectie);
                  return (
                    <details key={sec.sectie} className="fawkes-kennis__sectie" open>
                      <summary>
                        <span className="fawkes-kennis__sectieicoon" aria-hidden="true">
                          <SectieIcoon size={17} />
                        </span>
                        <span className="fawkes-kennis__sectienaam">
                          {sec.sectie === "" ? t("kennismaking.overigeAntwoorden") : sec.sectie}
                        </span>
                        <ChevronDown size={18} aria-hidden="true" className="fawkes-kennis__chevron" />
                      </summary>
                      <dl>
                        {sec.antwoorden.map((a, i) => {
                          const VraagIcoon = vraagIcoon(a.vraag);
                          return (
                            <div key={`${sec.sectie}-${i}`} className="fawkes-kennis__antwoord">
                              <dt>
                                <VraagIcoon size={15} aria-hidden="true" />
                                <span>{a.vraag}</span>
                              </dt>
                              <dd>
                                {a.antwoord == null || a.antwoord.trim() === ""
                                  ? t("kennismaking.nietIngevuld")
                                  : a.antwoord}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </details>
                  );
                })}

                <div className="fawkes-kennis__notitie">
                  <label htmlFor="kennis-notitie">{t("kennismaking.notitie")}</label>
                  <textarea
                    id="kennis-notitie"
                    rows={3}
                    value={notitie}
                    onChange={(e) => setNotitie(e.target.value)}
                    placeholder={t("kennismaking.notitie.hint")}
                  />
                  <Button variant="secondary" onClick={bewaarNotitie} disabled={bezig}>
                    {t("kennismaking.notitie.bewaar")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Welk icoon er voor een sectie staat.
 *
 * <p>Op de naam en niet op een sleutel: de secties zijn de staptitels die een asiel zelf verzint, en
 * die kunnen morgen anders heten. Herkent hij er geen, dan staat er een vraagteken -- een icoon dat
 * niets belooft is beter dan een icoon dat iets anders belooft.
 */
function sectieIcoon(sectie: string): ComponentType<LucideProps> {
  const s = sectie.toLowerCase();
  if (s.includes("jou") || s.includes("over je")) return UserRound;
  if (s.includes("huis") || s.includes("woning")) return Home;
  if (s.includes("dier") || s.includes("hond") || s.includes("kat") || s.includes("konijn")
      || s.includes("vogel") || s.includes("knaagdier")) return PawPrint;
  return MessageCircleQuestion;
}

/** Hetzelfde voor één vraag; de vraagtekst is het enige dat hier binnenkomt. */
function vraagIcoon(vraag: string | null): ComponentType<LucideProps> {
  const v = (vraag ?? "").toLowerCase();
  if (v.includes("e-mail") || v.includes("mailadres")) return Mail;
  if (v.includes("telefoon") || v.includes("bellen")) return Phone;
  if (v.includes("plaats") || v.includes("woon je")) return MapPin;
  if (v.includes("heet je") || v.includes("naam")) return UserRound;
  if (v.includes("woning") || v.includes("tuin") || v.includes("huis")) return Home;
  if (v.includes("kinderen") || v.includes("wonen er")) return Users;
  if (v.includes("dier") || v.includes("hond") || v.includes("kat")) return PawPrint;
  return MessageCircleQuestion;
}
